// ============================================================
// ReRunAiDialog.tsx
// ------------------------------------------------------------
// 2026-05-21: Kör AI igen på poster som redan har storagePath
// (PDF ligger redan i Firebase Storage). Ingen file-upload behövs
// — vi anropar aiAnalyzeReceipt(companyId, r, true) direkt och
// AI hämtar PDF:en från Storage via /api/storage-fetch (alt B).
//
// Skiljer sig från AiVatBatchDialog som kräver file-input
// för poster utan storagePath.
// ============================================================

import { useEffect, useRef, useState } from 'react'
import { aiAnalyzeReceipt, type CloudReceipt } from '../lib/cloudStore'

const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

interface Props {
  open: boolean
  onClose: () => void
  companyId: string
  companyName: string
  /** Poster som har storagePath men saknar moms-data */
  receipts: CloudReceipt[]
}

type Phase = 'idle' | 'running' | 'done'

interface PostResult {
  receiptId: string
  filename: string
  status: 'pending' | 'success' | 'failed' | 'skipped'
  error?: string
}

// 2026-05-23: sänkt 2 → 1 (sekventiellt) för att inte överskrida Claude's
// 2026-05-24: raised from 1 → 10 per request. Re-run jobs touch the same
// /api/storage-extract endpoint that has 3-retry backoff on 429, so up to 30
// in-flight calls/min worst case — still under tier-1 50K input-tokens/min.
// Bump down if you see 429 cascades in the log.
const MAX_CONCURRENT = 10
const CAT_LABEL: Record<CloudReceipt['category'], string> = {
  kundfakturor: 'Kundfakturor',
  leverantorsfakturor: 'Leverantörsfakturor',
  utlagg: 'Utlägg',
}

export function ReRunAiDialog({ open, onClose, companyId, companyName, receipts }: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [results, setResults] = useState<PostResult[]>([])
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [copied, setCopied] = useState(false)
  const cancelRef = useRef<boolean>(false)

  // Reset state när dialog stängs
  useEffect(() => {
    if (!open) {
      setPhase('idle')
      setResults([])
      setProgress({ done: 0, total: 0 })
      cancelRef.current = false
    }
  }, [open])

  // Escape stänger (om inte körning pågår)
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'running') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose, phase])

  if (!open) return null

  const totalAmount = receipts.reduce((s, r) => s + (r.amount ?? 0), 0)
  const byCategory = new Map<CloudReceipt['category'], CloudReceipt[]>()
  for (const r of receipts) {
    const arr = byCategory.get(r.category) ?? []
    arr.push(r)
    byCategory.set(r.category, arr)
  }

  async function processOne(r: CloudReceipt): Promise<PostResult> {
    try {
      const res = await aiAnalyzeReceipt(companyId, r, true)
      if (res.error && !res.updated) {
        return { receiptId: r.id, filename: r.filename, status: 'failed', error: res.error }
      }
      if (res.skipped) {
        return { receiptId: r.id, filename: r.filename, status: 'skipped', error: res.error }
      }
      return { receiptId: r.id, filename: r.filename, status: 'success' }
    } catch (e) {
      return { receiptId: r.id, filename: r.filename, status: 'failed', error: (e as Error).message }
    }
  }

  async function runBatch() {
    cancelRef.current = false
    setPhase('running')
    setProgress({ done: 0, total: receipts.length })

    const initial: PostResult[] = receipts.map((r) => ({
      receiptId: r.id, filename: r.filename, status: 'pending',
    }))
    setResults(initial)

    const updated = [...initial]
    let done = 0
    for (let i = 0; i < receipts.length; i += MAX_CONCURRENT) {
      if (cancelRef.current) break
      const chunk = receipts.slice(i, i + MAX_CONCURRENT)
      const settled = await Promise.all(
        chunk.map((r, ci) => processOne(r).then((res) => ({ res, idx: i + ci })))
      )
      for (const { res, idx } of settled) {
        updated[idx] = res
      }
      done += chunk.length
      setProgress({ done, total: receipts.length })
      setResults([...updated])
    }
    setPhase('done')
  }

  const successCount = results.filter((r) => r.status === 'success').length
  const failedCount = results.filter((r) => r.status === 'failed').length
  const skippedCount = results.filter((r) => r.status === 'skipped').length

  async function copyResults() {
    const lines = results.map((r) => {
      const status =
        r.status === 'success' ? 'OK' :
        r.status === 'failed' ? 'FAIL: ' + (r.error ?? 'fel') :
        r.status === 'skipped' ? 'SKIP' :
        'PENDING'
      return r.filename + '  →  ' + status
    })
    const text = '# Re-run AI — ' + companyName + ' (' + results.length + ' poster)\n' +
                 'Lyckade: ' + successCount + ' · Misslyckade: ' + failedCount + ' · Hoppade över: ' + skippedCount + '\n\n' +
                 lines.join('\n')
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const ta = document.createElement('textarea')
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      alert('Kopiering misslyckades — markera texten manuellt istället.')
    }
  }

  return (
    <div
      onClick={() => { if (phase !== 'running') onClose() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '2rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '1.25rem 1.5rem',
          width: 'min(720px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          color: 'var(--text)',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '0.75rem',
        }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>
            {'🔁 Kör AI igen — ' + companyName}
          </div>
          <button
            onClick={onClose}
            disabled={phase === 'running'}
            style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.3rem',
              cursor: phase === 'running' ? 'not-allowed' : 'pointer',
              padding: '0.25rem', opacity: phase === 'running' ? 0.3 : 1,
            }}
          >{'✕'}</button>
        </div>

        <div style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
          {receipts.length + ' poster har PDF i Storage och saknar moms-data ('
            + fmt(totalAmount) + ' kr totalt). Ingen uppladdning behövs — vi hämtar PDF:en från Storage.'}
        </div>

        {/* Sammanfattning per kategori */}
        <div style={{
          border: '1px solid var(--border)', borderRadius: 8,
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
        }}>
          {Array.from(byCategory.entries()).map(([cat, list]) => {
            const sum = list.reduce((s, r) => s + (r.amount ?? 0), 0)
            return (
              <div key={cat} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '0.25rem 0', fontSize: '0.85rem',
              }}>
                <span>{CAT_LABEL[cat]}</span>
                <span style={{ color: 'var(--text-muted)' }}>
                  {list.length + ' st · ' + fmt(sum) + ' kr'}
                </span>
              </div>
            )
          })}
        </div>

        {phase === 'running' && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.4rem' }}>
              {'Bearbetar ' + progress.done + ' / ' + progress.total + ' (max ' + MAX_CONCURRENT + ' samtidigt)'}
            </div>
            <div style={{
              height: 8, background: 'rgba(255,255,255,0.08)',
              borderRadius: 4, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: (progress.total > 0 ? (progress.done / progress.total) * 100 : 0) + '%',
                background: '#10b981', transition: 'width 0.2s ease',
              }} />
            </div>
            <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {'Lyckade: ' + successCount + ' · Misslyckade: ' + failedCount + ' · Hoppades över: ' + skippedCount}
            </div>
          </div>
        )}

        {phase === 'done' && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem',
            marginBottom: '0.75rem',
          }}>
            <div style={{
              padding: '0.5rem',
              background: 'rgba(16,185,129,0.1)',
              border: '1px solid #10b981',
              borderRadius: 6,
            }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Lyckade</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 600, color: '#10b981' }}>{successCount}</div>
            </div>
            <div style={{
              padding: '0.5rem',
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid #ef4444',
              borderRadius: 6,
            }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Misslyckade</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 600, color: '#ef4444' }}>{failedCount}</div>
            </div>
            <div style={{
              padding: '0.5rem',
              background: 'rgba(245,158,11,0.1)',
              border: '1px solid #f59e0b',
              borderRadius: 6,
            }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Hoppade över</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 600, color: '#f59e0b' }}>{skippedCount}</div>
            </div>
          </div>
        )}

        {/* Resultatlista — visa bara om vi har data */}
        {results.length > 0 && (
          <div style={{
            border: '1px solid var(--border)', borderRadius: 6,
            maxHeight: 250, overflowY: 'auto',
            marginBottom: '0.75rem', fontSize: '0.78rem',
          }}>
            {results.map((r) => {
              const color =
                r.status === 'success' ? '#10b981' :
                r.status === 'failed' ? '#ef4444' :
                r.status === 'skipped' ? '#f59e0b' :
                'var(--text-muted)'
              return (
                <div key={r.receiptId} style={{
                  display: 'flex', flexDirection: 'column',
                  padding: '0.35rem 0.6rem',
                  borderTop: '1px solid var(--border)',
                  gap: '0.15rem',
                }} title={r.filename}>
                  <div style={{
                    fontSize: '0.78rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'var(--text-muted)',
                  }}>{r.filename}</div>
                  <div style={{
                    color,
                    fontSize: '0.78rem',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}>
                    {r.status === 'success' ? '✓ OK' :
                     r.status === 'failed' ? '✗ ' + (r.error ?? 'fel') :
                     r.status === 'skipped' ? '· hoppade över' :
                     '…'}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Knappar */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          {results.length > 0 && phase !== 'idle' && (
            <button
              onClick={copyResults}
              style={{
                background: copied ? 'rgba(16,185,129,0.2)' : 'rgba(96,165,250,0.15)',
                color: copied ? '#10b981' : '#60a5fa',
                border: '1px solid ' + (copied ? '#10b981' : '#60a5fa'),
                borderRadius: 6, padding: '0.5rem 1rem',
                fontSize: '0.88rem', cursor: 'pointer',
              }}
            >{copied ? '✓ Kopierat!' : '📋 Kopiera resultatet'}</button>
          )}
          {phase === 'running' && (
            <button
              onClick={() => { cancelRef.current = true }}
              style={{
                background: 'rgba(239,68,68,0.15)', color: '#ef4444',
                border: '1px solid #ef4444', borderRadius: 6,
                padding: '0.5rem 1rem', fontSize: '0.88rem', cursor: 'pointer',
              }}
            >Avbryt körning</button>
          )}
          {phase !== 'running' && (
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.06)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 6,
                padding: '0.5rem 1rem', fontSize: '0.88rem', cursor: 'pointer',
              }}
            >Stäng</button>
          )}
          {phase === 'idle' && receipts.length > 0 && (
            <button
              onClick={runBatch}
              style={{
                background: '#10b981', color: '#fff', border: 'none',
                borderRadius: 6, padding: '0.5rem 1rem',
                fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
              }}
            >{'Kör nu på ' + receipts.length + ' poster'}</button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReRunAiDialog
